<template>
	<div class="page-container">
		<!-- 简览视图 -->
		<div v-if="isOverview" class="overview-content" :style="{ height: deviceHeight }">
			<swiper :options="swiperOption" ref="swiperRef" style="height: 100%">
				<swiper-slide
					v-for="slide in getSlideList"
					:key="slide.id"
					:data-slide-uuid="slide.id"
					:data-slide-module-id="slide.moduleId"
					class="overview-slide-ani-trigger"
				>
					<template v-if="slide.type === 'module'">
						<div
							class="overview-module-wrapper"
							:style="{ height: deviceHeight }"
							:key="getModuleKey(slide.data)"
						>
							<SmartDisplayC
								:compId="getModuleId(slide.data)"
								mode="overview"
								v-bind="getSlideProps(slide)"
								style="height: 100%"
							/>
						</div>
					</template>
					<template v-if="slide.type === 'emptyModule'">
						<div class="overview-module-wrapper" :style="{ height: deviceHeight }">
							<div class="rs-o-surf-container rs-o-surf-module-section">
								<SurfContentEmpty />
							</div>
						</div>
					</template>
					<template v-if="slide.type === 'footer'">
						<SmartLayoutC compId="layout_footer" mode="basic" v-bind="getFooterProps(slide)" />
					</template>
				</swiper-slide>
			</swiper>
		</div>

		<!-- 详情视图 -->
		<div v-else-if="isDetail" class="detail-content">
			<SurfContentEmpty v-if="!displayModule" />
			<SmartDisplayC
				v-else
				:compId="getModuleId(displayModule)"
				mode="full"
				v-bind="getModuleProps(displayModule)"
			/>
		</div>

		<!-- 缺省兜底 -->
		<div v-else class="default-content">
			<SurfContentEmpty />
		</div>
	</div>
</template>

<script setup>
import { computed, ref, onMounted, inject, watch, nextTick, onUnmounted, getCurrentInstance } from 'vue';
import { useModuleAttrs } from '@rebuild/hooks/useModuleAttrs';
import SmartDisplayC from '@rebuild/components/SmartDisplayC.vue';
import { useDisplayLogic, INJECT_KEY_OPENING_COMPLETE } from '@rebuild/materials/shared/page_container/useDisplayLogic';
import { useOverviewSwiper } from '@rebuild/materials/shared/page_container/useOverviewSwiper';
import { useSlideObserver } from '@rebuild/materials/shared/page_container/useSlideObserver';
import SmartLayoutC from '@rebuild/components/SmartLayoutC.vue';
import { Swiper, SwiperSlide } from 'vue-awesome-swiper';
import SwiperCore, { Mousewheel } from 'swiper';
import { get100vh, getUuid } from '@/utils/api';
import SurfContentEmpty from '@rebuild/materials/shared/common/surf/SurfContentEmpty.vue';
import { useBusEvent } from '@rebuild/hooks/useBusEvent';
import { useDeviceResize, DEVICE_RESIZE_DELAY } from '@rebuild/hooks/useDeviceResize';

const deviceHeight = ref(get100vh());
const vm = getCurrentInstance();

const { displayModuleList, displayModule, isOverview, isDetail } = useDisplayLogic();
const { getModuleKey, getModuleId, getModuleProps } = useModuleAttrs();

// 初始化 Swiper
SwiperCore.use([Mousewheel]);
const { swiperRef, swiperOption, setupResizeObserver, updateSwiper, slideToTarget } = useOverviewSwiper({
	slideModuleIdAttribute: 'data-slide-module-id'
});

useDeviceResize(() => {
	deviceHeight.value = get100vh();
	updateSwiper();
}, DEVICE_RESIZE_DELAY);

onMounted(() => {
	if (!isOverview.value) return;
	setupResizeObserver('.overview-content');
});

// 记录 Slide 可见状态
const slideVisibility = ref({});
const updateSlideVisibility = (slideId, isVisible) => {
	if (!slideId) return;
	vm.proxy.$set(slideVisibility.value, slideId, !!isVisible);
};
const getSlideProps = slide => {
	const baseProps = getModuleProps(slide.data) || {};
	const slideVisible = slideVisibility.value[slide.id] || false;
	return {
		...baseProps,
		slideVisible
	};
};
const getFooterProps = slide => {
	const slideVisible = slideVisibility.value[slide.id] || false;
	return {
		slideVisible
	};
};

const getSlideList = computed(() => {
	if (!isOverview.value) return [];

	const modules = [];
	if (displayModuleList.value.length) {
		displayModuleList.value.forEach(module => {
			modules.push({
				type: 'module',
				id: getUuid(6),
				data: module,
				moduleId: module.moduleId
			});
		});
	} else {
		modules.push({
			type: 'emptyModule',
			id: getUuid(6),
			data: null,
			moduleId: null
		});
	}

	const footer = [];
	footer.push({
		type: 'footer',
		id: getUuid(6),
		data: null
	});

	return [...modules, ...footer];
});

// 初始化 Slide 展示监听器
const { setupSlideObserver, cleanupSlideObserver } = useSlideObserver({ targetAttribute: 'data-slide-uuid' });
const openingComplete = inject(INJECT_KEY_OPENING_COMPLETE);
watch(
	openingComplete,
	val => {
		if (!isOverview.value) return;
		if (val) {
			nextTick(() => {
				// 监听 SwiperItem 的展示状态，仅当内容完整展示后执行动画
				setupSlideObserver((slideId, isVisible) => {
					updateSlideVisibility(slideId, isVisible);
				}, '.overview-slide-ani-trigger');
			});
		}
	},
	{ immediate: true }
);
onUnmounted(() => {
	cleanupSlideObserver();
});

// 根据消息ID，快速定位到到指定模块
const onSlideWithModuleId = data => {
	const { activeModuleId } = data;
	if (activeModuleId) slideToTarget(activeModuleId);
};
useBusEvent('once-message', onSlideWithModuleId, { once: true });
useBusEvent('iframe-message', onSlideWithModuleId);
</script>

<style lang="less" scoped>
.overview-module-wrapper {
	overflow: hidden;
}

.page-container {
	.detail-content {
		min-height: calc(100vh - var(--surf-nav-height));
		padding-bottom: 20px;
	}
	.default-content {
		height: calc(100vh - var(--surf-nav-height));
		display: flex;
		justify-content: center;
		align-items: center;
	}
}
</style>
